import { supabase } from '@/lib/supabaseClient';
import { updateJob, formatUsd, parseContractorDate } from '@/lib/jobsData';
import { fetchSettingsByKey, SETTINGS_KEYS } from '@/lib/settingsData';

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Store + rebate config
 *
 * Only Menards runs a standing per-receipt rebate (11% promo windows, pre-tax
 * subtotal, mail-in → merchandise credit check). Lowe's "5%" is a credit-card
 * discount taken at the register, and Home Depot's 2% is a Pro Xtra rebate
 * that requires enrollment + minimum spend — neither is automatic, so both
 * default to 0. Contractors enrolled in a program set their own rates in
 * Settings → Defaults (stored in percent on the `defaults` settings blob);
 * loadStoreRebateRates() applies the tenant's rates into the live table.
 * A printed rebate amount scanned off a receipt always wins over the rate.
 * ------------------------------------------------------------------------- */
export const DEFAULT_STORE_REBATES = {
  Menards: 0.11,
  Lowes: 0,
  'Home Depot': 0,
  Other: 0,
};

// Live rate table — mutated in place by loadStoreRebateRates() so every
// existing import (dialog UI, rebate math, CSV recompute) sees tenant rates.
export const STORE_REBATES = { ...DEFAULT_STORE_REBATES };

/**
 * Load the tenant's store rebate rates from settings into STORE_REBATES.
 * Call on receipts/rebates page load; safe to call repeatedly. Falls back to
 * the defaults for missing/invalid values. Returns a snapshot of the rates.
 */
export const loadStoreRebateRates = async () => {
  try {
    const res = await fetchSettingsByKey(SETTINGS_KEYS.DEFAULTS, null);
    const pcts = res?.value?.store_rebates;
    for (const k of Object.keys(STORE_REBATES)) {
      const n = Number(pcts?.[k]);
      STORE_REBATES[k] = Number.isFinite(n)
        ? Math.min(1, Math.max(0, n / 100))
        : DEFAULT_STORE_REBATES[k];
    }
    // Cache the Pro Xtra tracker config off the same settings fetch.
    const px = res?.value?.pro_xtra;
    if (px && typeof px === 'object') {
      _proXtraConfig = {
        enabled: !!px.enabled,
        threshold_usd: Number(px.threshold_usd) > 0 ? Number(px.threshold_usd) : 12500,
        period: px.period === 'annual' ? 'annual' : 'semiannual',
      };
    }
  } catch {
    /* keep whatever we have — defaults on first load */
  }
  return { ...STORE_REBATES };
};

/* ----------------------------------------------------------------------------
 * Home Depot Pro Xtra rebate tracker
 *
 * The Pro Xtra rebate pays a % back on qualifying PRE-TAX purchases once net
 * spend clears a program minimum within the rebate period. We track progress
 * toward that threshold from scanned Home Depot receipts (receipts only — the
 * manual material log can duplicate a receipted purchase, so it's excluded).
 * ------------------------------------------------------------------------- */
let _proXtraConfig = { enabled: false, threshold_usd: 12500, period: 'semiannual' };

export const getProXtraConfig = () => ({ ..._proXtraConfig });

/**
 * Current rebate-period bounds. Semi-annual = Jan–Jun / Jul–Dec; annual =
 * calendar year (programs vary — the threshold is configurable in Settings).
 */
export const proXtraPeriodBounds = (period = 'semiannual', now = new Date()) => {
  const y = now.getFullYear();
  if (period === 'annual') {
    return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59) };
  }
  return now.getMonth() < 6
    ? { start: new Date(y, 0, 1), end: new Date(y, 5, 30, 23, 59, 59) }
    : { start: new Date(y, 6, 1), end: new Date(y, 11, 31, 23, 59, 59) };
};

/**
 * Sum this period's Home Depot PRE-TAX spend across jobs' receipts and
 * compare to the threshold. Per receipt: subtotal when scanned, else
 * amount − tax, else amount (older entries have no split — best available).
 */
export const computeProXtraProgress = (
  jobs = [],
  { threshold = 12500, period = 'semiannual', now = new Date() } = {}
) => {
  const { start, end } = proXtraPeriodBounds(period, now);
  let spend = 0;
  let receiptCount = 0;
  for (const job of jobs) {
    const receipts = Array.isArray(job?.receipts) ? job.receipts : [];
    for (const r of receipts) {
      if ((r?.store || '') !== 'Home Depot') continue;
      const d = parseContractorDate(r?.date);
      if (!d || d < start || d > end) continue;
      const sub = Number(r?.subtotal);
      const amt = Number(r?.amount) || 0;
      const tax = Number(r?.tax) || 0;
      const preTax = sub > 0 ? sub : Math.max(0, amt - tax);
      if (preTax <= 0) continue;
      spend += preTax;
      receiptCount += 1;
    }
  }
  spend = Math.round(spend * 100) / 100;
  const th = Number(threshold) > 0 ? Number(threshold) : 12500;
  return {
    spend,
    threshold: th,
    remaining: Math.max(0, Math.round((th - spend) * 100) / 100),
    pct: Math.min(1, spend / th),
    qualified: spend >= th,
    receiptCount,
    periodStart: start,
    periodEnd: end,
  };
};

export const STORES = ['Menards', 'Lowes', 'Home Depot', 'Other'];

export const computeRebate = (store, amount) => {
  const rate = STORE_REBATES[store] ?? 0;
  const amt = Number(amount) || 0;
  return Math.round(amt * rate * 100) / 100;
};

/**
 * Correct rebate for a receipt. The rebate base is the PRE-TAX subtotal
 * (Menards' 11% is calculated pre-tax), and when the receipt prints an exact
 * rebate amount (Menards does), we use that figure verbatim.
 *   - rebateAmount given  → use it (most accurate)
 *   - else subtotal × rate (pre-tax)
 *   - else (total − tax) × rate  → fallback when only the total is known
 */
export const receiptRebate = ({
  store,
  subtotal = 0,
  total = 0,
  tax = 0,
  rebateAmount = null,
}) => {
  if (Number(rebateAmount) > 0) return Math.round(Number(rebateAmount) * 100) / 100;
  const rate = STORE_REBATES[store] ?? 0;
  const sub = Number(subtotal) || 0;
  const base = sub > 0 ? sub : Math.max(0, (Number(total) || 0) - (Number(tax) || 0));
  return Math.round(base * rate * 100) / 100;
};

export const formatPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  return `${(n * 100).toFixed(0)}%`;
};

/* ----------------------------------------------------------------------------
 * Fetch jobs + flatten receipts
 * ------------------------------------------------------------------------- */
const JOB_FIELDS =
  'id,name,client,client_id,address,status,phase,materials_spent,material_log,receipts,contract,created_at';

export const fetchReceiptsData = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const fetchActiveJobsForReceipts = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select('id,name,client,status')
    .eq('status', 'active')
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Flatten embedded jobs.receipts[] into top-level rows enriched with job context.
 */
export const flattenReceipts = (jobs = []) => {
  const rows = [];
  for (const job of jobs) {
    const receipts = Array.isArray(job?.receipts) ? job.receipts : [];
    receipts.forEach((r, idx) => {
      const date = parseContractorDate(r?.date);
      rows.push({
        row_key: `${job.id}-${r?.id || 'noid'}-${idx}`,
        receipt_id: r?.id || null,
        idx,
        job_id: job.id,
        job_name: job.name || 'Untitled job',
        client: job.client || '',
        store: r?.store || 'Other',
        amount: Number(r?.amount) || 0,
        rebate: Number(r?.rebate) || 0,
        rebate_rate: Number(r?.rebate_rate) || 0,
        desc: r?.desc || '',
        date: r?.date || '',
        date_obj: date,
        photo: r?.photo || null,
        photo_path: r?.photo_path || null,
        created_at: r?.created_at || null,
      });
    });
  }
  // Most recent first
  rows.sort((a, b) => {
    const ta = a.date_obj ? a.date_obj.getTime() : 0;
    const tb = b.date_obj ? b.date_obj.getTime() : 0;
    if (ta !== tb) return tb - ta;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });
  return rows;
};

/* ----------------------------------------------------------------------------
 * Stats
 * ------------------------------------------------------------------------- */
export const computeReceiptStats = (rows = []) => {
  let total = 0;
  let rebate = 0;
  const byStore = {};
  const byJob = {};
  for (const r of rows) {
    total += r.amount;
    rebate += r.rebate;
    byStore[r.store] = (byStore[r.store] || 0) + r.amount;
    byJob[r.job_id] = byJob[r.job_id] || {
      job_id: r.job_id,
      job_name: r.job_name,
      client: r.client,
      total: 0,
      rebate: 0,
      count: 0,
    };
    byJob[r.job_id].total += r.amount;
    byJob[r.job_id].rebate += r.rebate;
    byJob[r.job_id].count += 1;
  }
  return {
    count: rows.length,
    total,
    rebate,
    netCost: total - rebate,
    byStore,
    byJob: Object.values(byJob).sort((a, b) => b.total - a.total),
  };
};

/* ----------------------------------------------------------------------------
 * Filter
 * ------------------------------------------------------------------------- */
export const applyReceiptFilter = (
  rows = [],
  { search = '', store = 'all', jobId = 'all' } = {}
) => {
  const q = (search || '').trim().toLowerCase();
  return rows.filter((r) => {
    if (store !== 'all' && r.store !== store) return false;
    if (jobId !== 'all' && r.job_id !== jobId) return false;
    if (!q) return true;
    const hay = [r.job_name, r.client, r.store, r.desc]
      .map((v) => (v || '').toLowerCase())
      .join(' | ');
    return hay.includes(q);
  });
};

/* ----------------------------------------------------------------------------
 * Receipt photo storage
 *
 * Photos live in the private `receipt-photos` bucket (migration 049), path
 * `<tenant>/receipts/<job_id>/<receipt_id>.jpg` — the receipt entry stores only
 * `photo_path`. Storing photos INLINE as data URLs made bucket-job rows grow
 * until updates hit the DB statement timeout ("canceling statement due to
 * statement timeout" on save), because logging a receipt rewrites the whole
 * receipts[] array. If an upload fails we fall back to a COMPRESSED inline
 * photo — the flow never dead-ends, the row just grows slower.
 * ------------------------------------------------------------------------- */
const RECEIPT_BUCKET = 'receipt-photos';

let _tenantId = null;
const getTenantId = async () => {
  if (_tenantId) return _tenantId;
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes?.user?.id;
  if (!uid) return null;
  const { data } = await supabase
    .from('profiles')
    .select('contractor_id')
    .eq('id', uid)
    .single();
  _tenantId = data?.contractor_id || null;
  return _tenantId;
};

const dataUrlToBlob = (dataUrl) => {
  const m = String(dataUrl || '').match(/^data:([^;]+);base64,(.+)$/);
  if (!m) return null;
  const bin = atob(m[2]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: m[1] });
};

/** Upload a receipt photo (data URL) → storage path, or null on any failure. */
const uploadReceiptPhoto = async (dataUrl, jobId, receiptId) => {
  try {
    const tenant = await getTenantId();
    if (!tenant) return null;
    const scaled = await scaleDataUrl(dataUrl, 1600);
    const blob = dataUrlToBlob(scaled);
    if (!blob) return null;
    const path = `${tenant}/receipts/${jobId}/${receiptId}.jpg`;
    const { error } = await supabase.storage
      .from(RECEIPT_BUCKET)
      .upload(path, blob, { contentType: 'image/jpeg', upsert: true });
    if (error) return null;
    return path;
  } catch {
    return null;
  }
};

/**
 * Resolve a receipt's photo for display: legacy inline data URLs pass through;
 * a storage path becomes a short-lived signed URL. Returns null when neither.
 */
export const getReceiptPhotoUrl = async ({ photo = null, photo_path: photoPath = null } = {}) => {
  if (photo && String(photo).startsWith('data:')) return photo;
  if (!photoPath) return null;
  try {
    const { data, error } = await supabase.storage
      .from(RECEIPT_BUCKET)
      .createSignedUrl(photoPath, 3600);
    if (error) return null;
    return data?.signedUrl || null;
  } catch {
    return null;
  }
};

/**
 * One-time lazy migration: move legacy INLINE photos out of jobs.receipts[]
 * into the bucket, per job (upload each, then a single small row update).
 * Best-effort and silent — failures leave the job untouched for next time.
 * Returns the number of jobs migrated.
 */
export const migrateInlineReceiptPhotos = async (jobs = []) => {
  let migrated = 0;
  for (const job of jobs) {
    const receipts = Array.isArray(job?.receipts) ? job.receipts : [];
    const inline = receipts.filter(
      (r) => typeof r?.photo === 'string' && r.photo.startsWith('data:')
    );
    if (inline.length === 0) continue;
    try {
      const next = [];
      let changed = false;
      for (const r of receipts) {
        if (typeof r?.photo === 'string' && r.photo.startsWith('data:')) {
          const path = await uploadReceiptPhoto(r.photo, job.id, r.id || genId('REC'));
          if (path) {
            next.push({ ...r, photo: null, photo_path: path });
            changed = true;
            continue;
          }
        }
        next.push(r);
      }
      if (changed) {
        await updateJob(job.id, { receipts: next });
        migrated += 1;
      }
    } catch {
      /* leave this job for the next pass */
    }
  }
  return migrated;
};

/* ----------------------------------------------------------------------------
 * Writes — add/remove receipts on jobs.receipts[]
 * ------------------------------------------------------------------------- */
const genId = (prefix) => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().split('-')[0].toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${id}`;
};

export const logReceipt = async (
  job,
  {
    store = 'Other',
    amount = 0,
    subtotal = 0,
    tax = 0,
    rebateAmount = null,
    rebateNumber = '',
    desc = '',
    date = '',
    photo = null,
  } = {}
) => {
  if (!job?.id) throw new Error('Missing job id');
  const total = Number(amount) || 0; // total sale WITH tax = job cost
  const sub = Number(subtotal) || 0; // pre-tax subtotal = rebate base
  const taxAmt = Number(tax) || 0;
  const rate = STORE_REBATES[store] ?? 0;
  const nowIso = new Date().toISOString();
  // Phase 1: new receipts on rebate-eligible stores enter the lifecycle at
  // `logged` automatically — there is NO manual status step at creation.
  // Non-rebate stores skip the lifecycle entirely (no rebate_status field).
  const isRebateEligible = rate > 0;
  // Rebate is on the PRE-TAX subtotal (or the printed amount) — never the
  // tax-inclusive total.
  const rebate = receiptRebate({ store, subtotal: sub, total, tax: taxAmt, rebateAmount });
  const entryId = genId('REC');
  // Photo → storage bucket; the row keeps only the path. On upload failure,
  // fall back to a compressed inline copy so the save still succeeds.
  let photoPath = null;
  let inlinePhoto = null;
  if (photo) {
    photoPath = await uploadReceiptPhoto(photo, job.id, entryId);
    if (!photoPath) inlinePhoto = await scaleDataUrl(photo, 1280);
  }
  const entry = {
    id: entryId,
    store: String(store || 'Other'),
    amount: total,
    subtotal: sub > 0 ? sub : null,
    tax: taxAmt > 0 ? taxAmt : null,
    rebate,
    rebate_rate: rate,
    ...(rebateNumber ? { rebate_number: String(rebateNumber).slice(0, 40) } : {}),
    desc: String(desc || '').slice(0, 200),
    date: date || new Date().toISOString().slice(0, 10),
    photo: inlinePhoto,
    ...(photoPath ? { photo_path: photoPath } : {}),
    created_at: nowIso,
    ...(isRebateEligible
      ? { rebate_status: 'logged', rebate_logged_at: nowIso }
      : {}),
  };
  const receipts = [entry, ...(Array.isArray(job.receipts) ? job.receipts : [])];
  return updateJob(job.id, { receipts });
};

export const deleteReceipt = async (job, receiptId) => {
  if (!job?.id) throw new Error('Missing job id');
  const all = Array.isArray(job.receipts) ? job.receipts : [];
  const removed = all.find((r) => r?.id === receiptId);
  const receipts = all.filter((r) => r?.id !== receiptId);
  const updated = await updateJob(job.id, { receipts });
  // Best-effort storage cleanup — the row is already consistent either way.
  if (removed?.photo_path) {
    try {
      await supabase.storage.from(RECEIPT_BUCKET).remove([removed.photo_path]);
    } catch {
      /* orphaned object is harmless */
    }
  }
  return updated;
};

/* ----------------------------------------------------------------------------
 * Photo helper (file -> data URL). For now we just store data URL inline; this
 * keeps the implementation simple and self-contained without a storage bucket.
 * ------------------------------------------------------------------------- */
export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/* ----------------------------------------------------------------------------
 * Receipt scanning (AI vision) — proposes fields; the contractor confirms.
 * ------------------------------------------------------------------------- */

// Map a raw merchant string to one of STORES so the rebate rate is auto-picked.
export const normalizeStore = (raw) => {
  const s = String(raw || '').toLowerCase();
  if (s.includes('menard')) return 'Menards';
  if (s.includes('home depot') || s.includes('homedepot')) return 'Home Depot';
  if (s.includes('lowe')) return 'Lowes';
  return 'Other';
};

// Downscale a data URL before sending to the vision model — receipts are
// text-dense, so ~1600px long-edge keeps digits legible while staying well
// under the per-image size limit and cutting cost. Falls back to the original
// on any failure.
const scaleDataUrl = (dataUrl, maxDim = 1600) =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.onerror = () => resolve(dataUrl);
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        if (scale >= 1) return resolve(dataUrl);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL('image/jpeg', 0.85));
      };
      img.src = dataUrl;
    } catch {
      resolve(dataUrl);
    }
  });

/**
 * Scan a receipt photo (data URL) via the `receipt-scan` Edge Function.
 * Returns { ok, store, amount, date, desc, tax, confidence } — a PROPOSAL for
 * the contractor to confirm. Never throws to the UI on a scan miss.
 */
export const scanReceipt = async (dataUrl) => {
  if (!dataUrl) return { ok: false, error: 'no_image' };
  try {
    const image = await scaleDataUrl(dataUrl, 1600);
    const { data, error } = await supabase.functions.invoke('receipt-scan', {
      body: { image },
    });
    // Surface why a scan didn't produce fields so the UI can explain it and the
    // console shows a diagnosable reason (function not deployed, missing API
    // key, CORS, unreadable image, etc.) instead of failing silently.
    if (error) {
      let detail = error?.message || 'function_error';
      try {
        const body = await error?.context?.json?.();
        if (body?.error) detail = body.error;
      } catch (_) {
        /* context not JSON — keep the message we have */
      }
      // eslint-disable-next-line no-console
      console.warn('[receipt-scan] invoke error:', detail, error);
      return { ok: false, error: detail };
    }
    if (!data?.ok) {
      // eslint-disable-next-line no-console
      console.warn('[receipt-scan] function returned not-ok:', data);
      return { ok: false, error: data?.error || 'scan_failed' };
    }
    if (!data.receipt) return { ok: false, error: 'no_receipt' };
    const r = data.receipt;
    const store = r.merchant_raw
      ? normalizeStore(r.merchant_raw)
      : STORES.includes(r.store)
      ? r.store
      : 'Other';
    return {
      ok: true,
      store,
      subtotal: r.subtotal != null ? String(r.subtotal) : '',
      tax: r.tax != null ? String(r.tax) : '',
      total: r.total != null ? String(r.total) : '',
      rebateAmount: r.rebate_amount != null ? Number(r.rebate_amount) : null,
      rebateNumber: r.rebate_number || '',
      lineItems: Array.isArray(r.line_items)
        ? r.line_items
            .map((li) => ({
              desc: String(li?.description || '').slice(0, 60),
              amount: Number(li?.amount) || 0,
            }))
            .filter((li) => li.desc && li.amount > 0)
        : [],
      date: r.date || '',
      desc: r.description || '',
      confidence: Number(r.confidence) || 0,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[receipt-scan] exception:', e);
    return { ok: false, error: e?.message || 'exception' };
  }
};

/* ----------------------------------------------------------------------------
 * Split a receipt across jobs
 *
 * Given line items each tagged with a jobId, group them by job and prorate the
 * receipt's single tax figure (and, when the store prints an exact rebate, that
 * rebate) across the jobs by each job's share of the pre-tax subtotal. Each
 * group becomes its own receipt on its own job, so job costing and the rebate
 * tracker stay correct per job. Pure function — no I/O.
 *
 * items: [{ desc, amount, jobId }]   opts: { tax, rebateAmount }
 * returns: [{ jobId, items:[{desc,amount}], subtotal, tax, rebateAmount|null }]
 * ------------------------------------------------------------------------- */
export const splitReceiptByJob = (
  items = [],
  { tax = 0, rebateAmount = null } = {}
) => {
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const taxTotal = r2(tax);
  const printedRebate = Number(rebateAmount) > 0 ? r2(rebateAmount) : null;

  const clean = (Array.isArray(items) ? items : []).filter(
    (it) => it && it.jobId && Number(it.amount) > 0
  );
  const totalSubtotal = r2(clean.reduce((s, it) => s + Number(it.amount), 0));

  const byJob = new Map();
  for (const it of clean) {
    if (!byJob.has(it.jobId)) {
      byJob.set(it.jobId, { jobId: it.jobId, items: [], subtotal: 0 });
    }
    const g = byJob.get(it.jobId);
    g.items.push({ desc: String(it.desc || ''), amount: r2(it.amount) });
    g.subtotal = r2(g.subtotal + Number(it.amount));
  }
  const groups = Array.from(byJob.values());

  // Prorate `total` across groups by subtotal share; the last group absorbs the
  // rounding remainder so the parts sum exactly to `total`.
  const distribute = (total) => {
    if (!total || totalSubtotal <= 0) return groups.map(() => 0);
    let acc = 0;
    return groups.map((g, i) => {
      if (i === groups.length - 1) return r2(total - acc);
      const part = r2(total * (g.subtotal / totalSubtotal));
      acc = r2(acc + part);
      return part;
    });
  };
  const taxParts = distribute(taxTotal);
  const rebateParts = printedRebate != null ? distribute(printedRebate) : null;

  return groups.map((g, i) => ({
    jobId: g.jobId,
    items: g.items,
    subtotal: g.subtotal,
    tax: taxParts[i],
    rebateAmount: rebateParts ? rebateParts[i] : null,
  }));
};
